import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Copy, Check, X, Mail, Power, RefreshCw, ChevronDown, ChevronUp, Trash2, AlertTriangle,
} from "lucide-react";
import { COUNTRIES } from "../../data/countryData";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const INDUSTRIES = [
  "Technology",
  "Financial Services",
  "Healthcare",
  "Manufacturing",
  "Retail",
  "Education",
  "Professional Services",
  "Construction",
  "Hospitality",
  "Media & Entertainment",
  "Non-profit",
  "Government",
  "Other",
];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const inputCls =
  "w-full p-2.5 rounded-lg bg-black/40 border border-white/20 text-white placeholder-white/30 " +
  "outline-none focus:border-[#00FF99]/60 text-sm";

const primaryBtn =
  "inline-flex items-center gap-2 bg-[#00FF99] text-black font-semibold px-4 py-2 rounded-lg " +
  "hover:bg-[#00cc7a] transition disabled:opacity-50";

const secondaryBtn =
  "inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white/90 px-3 py-2 " +
  "rounded-lg hover:bg-white/10 transition text-sm";

const dangerBtn =
  "inline-flex items-center gap-2 bg-red-500/10 border border-red-500/40 text-red-300 " +
  "px-3 py-2 rounded-lg hover:bg-red-500/20 transition text-sm";

const deleteBtn =
  "inline-flex items-center gap-2 px-3 py-2 rounded-lg transition text-sm " +
  "bg-[rgba(255,50,50,0.1)] border border-[rgba(255,50,50,0.3)] text-[#ff5555] " +
  "hover:bg-[rgba(255,50,50,0.2)]";

const deleteFilledBtn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold " +
  "bg-[#ff5555] text-white hover:bg-[#ff3333] disabled:opacity-50";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Modal({ title, onClose, children, wide = false, dismissOnBackdrop = true }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={dismissOnBackdrop ? onClose : undefined}
    >
      <div
        className={`${cardCls} ${wide ? "max-w-2xl" : "max-w-md"} w-full p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#00FF99]">{title}</h3>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="text-xs text-[#00FF99]/70 uppercase tracking-wider font-semibold pt-2 pb-1 border-b border-white/5 mb-2">
      {children}
    </div>
  );
}

function CreateOrgForm({ onCreated, onCancel }) {
  // Default country = first in list (United Kingdom)
  const defaultCountry = COUNTRIES[0];

  const [form, setForm] = useState({
    name: "",
    legal_name: "",
    industry: "",
    slug: "",

    countryCode: defaultCountry.code,
    country: defaultCountry.name,
    currency: defaultCountry.currency,
    timezone: defaultCountry.timezone,
    registration_number: "",
    tax_id: "",

    email: "",
    phone: "",
    website: "",

    admin_poc_name: "",
    admin_poc_email: "",
    admin_poc_phone: "",

    finance_poc_name: "",
    finance_poc_email: "",
    finance_poc_phone: "",

    primary_color: "#00FF99",
    tagline: "",
  });
  const [slugDirty, setSlugDirty]     = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === form.countryCode) || defaultCountry,
    [form.countryCode, defaultCountry]
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => ({
      ...f,
      name,
      slug: slugDirty ? f.slug : slugify(name),
    }));
  };

  const onSlugChange = (e) => {
    setSlugDirty(true);
    setForm((f) => ({ ...f, slug: e.target.value }));
  };

  const onCountryChange = (e) => {
    const code = e.target.value;
    const c = COUNTRIES.find((x) => x.code === code) || defaultCountry;
    setForm((f) => ({
      ...f,
      countryCode: c.code,
      country: c.name,
      currency: c.currency,
      timezone: c.timezone,
    }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())            errs.name = "Required";
    if (!form.slug.trim())            errs.slug = "Required";
    if (!form.country)                errs.country = "Required";
    if (!form.admin_poc_name.trim())  errs.admin_poc_name = "Required";
    if (!form.admin_poc_email.trim()) errs.admin_poc_email = "Required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.admin_poc_email.trim())) {
      errs.admin_poc_email = "Invalid email";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;

    setBusy(true);
    try {
      // Strip empty strings → null for optional fields
      const norm = (v) => {
        const t = (v || "").trim();
        return t === "" ? null : t;
      };
      const body = {
        name:                 form.name.trim(),
        slug:                 form.slug.trim().toLowerCase(),
        legal_name:           norm(form.legal_name),
        industry:             norm(form.industry),
        country:              norm(form.country),
        currency:             form.currency || "GBP",
        timezone:             form.timezone || "Europe/London",
        registration_number:  norm(form.registration_number),
        tax_id:               norm(form.tax_id),
        email:                norm(form.email),
        phone:                norm(form.phone),
        website:              norm(form.website),
        primary_color:        norm(form.primary_color),
        tagline:              norm(form.tagline),
        admin_poc_name:       norm(form.admin_poc_name),
        admin_poc_email:      norm(form.admin_poc_email),
        admin_poc_phone:      norm(form.admin_poc_phone),
        finance_poc_name:     norm(form.finance_poc_name),
        finance_poc_email:    norm(form.finance_poc_email),
        finance_poc_phone:    norm(form.finance_poc_phone),
      };
      const res = await fetch(`${API_BASE}/api/onboarding/orgs`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Surface API errors — slug-uniqueness conflicts come back here.
        setError(data.detail || `Failed to create organisation (${res.status})`);
        setBusy(false);
        return;
      }
      onCreated(data);
    } catch {
      setError("Network error — could not create organisation.");
      setBusy(false);
    }
  };

  // Tiny field-level error label
  const FE = ({ k }) => fieldErrors[k]
    ? <span className="text-red-400 text-[11px] mt-0.5 block">{fieldErrors[k]}</span>
    : null;

  return (
    <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1 -mr-1">
      {/* SECTION 1 — Basic Info */}
      <SectionHeader>Basic Info</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs text-white/60 block mb-1">Organisation Name *</label>
          <input className={inputCls} value={form.name} onChange={onNameChange} placeholder="Acme Corp" />
          <FE k="name" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Legal Name</label>
          <input className={inputCls} value={form.legal_name} onChange={set("legal_name")} placeholder="Acme Corp Ltd" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Slug *</label>
          <input
            className={inputCls + " font-mono"}
            value={form.slug}
            onChange={onSlugChange}
            placeholder="acme-corp"
          />
          <FE k="slug" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-white/60 block mb-1">Industry</label>
          <select className={inputCls} value={form.industry} onChange={set("industry")}>
            <option value="">— Select —</option>
            {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>

      {/* SECTION 2 — Location & Compliance */}
      <SectionHeader>Location & Compliance</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/60 block mb-1">Country *</label>
          <select className={inputCls} value={form.countryCode} onChange={onCountryChange}>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
          <FE k="country" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Currency</label>
          <input
            className={inputCls + " opacity-70 cursor-not-allowed"}
            readOnly
            value={`${selectedCountry.currencySymbol} ${form.currency}`}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-white/60 block mb-1">Timezone</label>
          <select className={inputCls} value={form.timezone} onChange={set("timezone")}>
            {selectedCountry.timezones.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">{selectedCountry.regLabel}</label>
          <input
            className={inputCls}
            value={form.registration_number}
            onChange={set("registration_number")}
            placeholder={selectedCountry.regPlaceholder}
          />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">{selectedCountry.taxLabel}</label>
          <input
            className={inputCls}
            value={form.tax_id}
            onChange={set("tax_id")}
            placeholder={selectedCountry.taxPlaceholder}
          />
        </div>
      </div>

      {/* SECTION 3 — Contact Details */}
      <SectionHeader>Contact Details</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/60 block mb-1">Organisation Email</label>
          <input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="hello@acme.com" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Organisation Phone</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+44 20 7946 0001" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-white/60 block mb-1">Website</label>
          <input className={inputCls} value={form.website} onChange={set("website")} placeholder="https://acme.com" />
        </div>
      </div>

      {/* SECTION 4 — Admin POC */}
      <SectionHeader>Admin Point of Contact</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-white/60 block mb-1">Name *</label>
          <input className={inputCls} value={form.admin_poc_name} onChange={set("admin_poc_name")} placeholder="Jane Smith" />
          <FE k="admin_poc_name" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Email *</label>
          <input className={inputCls} type="email" value={form.admin_poc_email} onChange={set("admin_poc_email")} placeholder="jane@acme.com" />
          <FE k="admin_poc_email" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Phone</label>
          <input className={inputCls} value={form.admin_poc_phone} onChange={set("admin_poc_phone")} />
        </div>
      </div>

      {/* SECTION 5 — Finance POC */}
      <SectionHeader>Finance Point of Contact</SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-white/60 block mb-1">Name</label>
          <input className={inputCls} value={form.finance_poc_name} onChange={set("finance_poc_name")} placeholder="John Doe" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Email</label>
          <input className={inputCls} type="email" value={form.finance_poc_email} onChange={set("finance_poc_email")} placeholder="finance@acme.com" />
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Phone</label>
          <input className={inputCls} value={form.finance_poc_phone} onChange={set("finance_poc_phone")} />
        </div>
      </div>

      {/* SECTION 6 — Branding (collapsible) */}
      <button
        type="button"
        onClick={() => setShowBranding((s) => !s)}
        className="w-full flex items-center justify-between text-xs text-[#00FF99]/70 uppercase tracking-wider font-semibold pt-3 pb-1 border-b border-white/5 mb-2"
      >
        <span>Branding {showBranding ? "" : "(optional)"}</span>
        {showBranding ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {showBranding && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-white/60 block mb-1">Primary Color</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={form.primary_color}
                onChange={set("primary_color")}
                className="h-10 w-12 rounded cursor-pointer bg-transparent border border-white/20"
              />
              <input
                className={inputCls + " font-mono"}
                value={form.primary_color}
                onChange={set("primary_color")}
                placeholder="#00FF99"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60 block mb-1">Tagline</label>
            <input className={inputCls} value={form.tagline} onChange={set("tagline")} placeholder="Workforce, intelligently." />
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-[#0d0d0d] -mx-1 px-1">
        <button type="button" onClick={onCancel} className={secondaryBtn}>Cancel</button>
        <button type="submit" disabled={busy} className={primaryBtn}>
          {busy ? "Creating…" : "Create Organisation"}
        </button>
      </div>
    </form>
  );
}

function InviteAdminForm({ org, onDone, onCancel }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [expiresHours, setExpiresHours] = useState(72);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Email is required."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/onboarding/invites`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          role,
          org_id: org.id,
          expires_hours: Number(expiresHours) || 72,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Failed to create invite.");
        setBusy(false);
        return;
      }
      setResult(data);
    } catch {
      setError("Network error.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  if (result) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-white/70">
          Invite created for <span className="text-white">{result.email}</span>.
          Share this link with them:
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={result.invite_url}
            readOnly
            className={`${inputCls} font-mono text-xs`}
            onFocus={(e) => e.target.select()}
          />
          <button onClick={copy} className={secondaryBtn}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="text-xs text-white/50">
          Expires {formatDate(result.expires_at)}.
        </p>
        <div className="flex justify-end pt-2">
          <button onClick={onDone} className={primaryBtn}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-white/60">
        Inviting into <span className="text-[#00FF99]">{org.name || org.slug}</span>
      </p>
      <div>
        <label className="text-xs text-white/60 block mb-1">Email</label>
        <input
          className={inputCls}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@example.com"
          autoFocus
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/60 block mb-1">Role</label>
          <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="admin">admin</option>
            <option value="executive">executive</option>
            <option value="manager">manager</option>
            <option value="staff">staff</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Expires (hours)</label>
          <input
            className={inputCls}
            type="number"
            min={1}
            max={720}
            value={expiresHours}
            onChange={(e) => setExpiresHours(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className={secondaryBtn}>Cancel</button>
        <button type="submit" disabled={busy} className={primaryBtn}>
          {busy ? "Creating…" : "Create Invite"}
        </button>
      </div>
    </form>
  );
}

export default function SuperadminOrgs() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [inviteOrg, setInviteOrg] = useState(null);
  const [deleteOrg, setDeleteOrg] = useState(null);   // org pending confirmation
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  // auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/onboarding/orgs`, { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        setError(data?.detail || "Failed to load organisations.");
        setOrgs([]);
      } else {
        setOrgs(Array.isArray(data) ? data : []);
      }
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (org) => {
    const prev = org.is_active;
    // optimistic update
    setOrgs((rows) =>
      rows.map((r) => (r.id === org.id ? { ...r, is_active: !prev } : r))
    );
    try {
      const res = await fetch(
        `${API_BASE}/api/onboarding/orgs/${org.id}/toggle`,
        { method: "PATCH", headers: authHeaders() }
      );
      if (!res.ok) throw new Error();
    } catch {
      // revert on failure
      setOrgs((rows) =>
        rows.map((r) => (r.id === org.id ? { ...r, is_active: prev } : r))
      );
    }
  };

  const confirmDeleteOrg = async () => {
    if (!deleteOrg) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/onboarding/orgs/${deleteOrg.id}?force=true`,
        { method: "DELETE", headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setDeleteError(data.detail || "Failed to delete organisation.");
        return;
      }
      setOrgs((rows) => rows.filter((r) => r.id !== deleteOrg.id));
      setDeleteOrg(null);
      setToast({ message: "Organisation deleted", tone: "ok" });
    } catch {
      setDeleteError("Network error.");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button onClick={load} className={secondaryBtn} title="Refresh">
          <RefreshCw size={16} />
        </button>
        <button onClick={() => setShowCreate(true)} className={primaryBtn}>
          <Plus size={16} /> Create Organisation
        </button>
      </div>

      <div className={`${cardCls} overflow-hidden`}>
          {loading ? (
            <div className="p-8 text-center text-white/60">Loading organisations…</div>
          ) : error ? (
            <div className="p-8 text-center text-red-400">{error}</div>
          ) : orgs.length === 0 ? (
            <div className="p-8 text-center text-white/60">No organisations yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/60 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">Ref</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Slug</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Created</th>
                    <th className="text-left px-4 py-3">Users</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((org) => (
                    <tr key={org.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                      <td className="px-4 py-3 text-white/80 font-mono text-xs whitespace-nowrap">
                        {org.reference_number || "—"}
                      </td>
                      <td className="px-4 py-3 text-white">{org.name || "—"}</td>
                      <td className="px-4 py-3 text-white/70 font-mono text-xs">{org.slug || "—"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            "inline-block px-2 py-0.5 rounded-full text-xs " +
                            (org.is_active
                              ? "bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]/30"
                              : "bg-white/5 text-white/50 border border-white/10")
                          }
                        >
                          {org.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/60">{formatDate(org.created_at)}</td>
                      <td className="px-4 py-3 text-white/70">{org.user_count ?? 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setInviteOrg(org)}
                            className={secondaryBtn}
                            title="Invite Admin"
                          >
                            <Mail size={14} /> Invite
                          </button>
                          <button
                            onClick={() => toggleActive(org)}
                            className={org.is_active ? dangerBtn : secondaryBtn}
                            title={org.is_active ? "Deactivate" : "Activate"}
                          >
                            <Power size={14} />
                            {org.is_active ? "Deactivate" : "Activate"}
                          </button>
                          <button
                            onClick={() => { setDeleteError(""); setDeleteOrg(org); }}
                            className={deleteBtn}
                            title="Delete organisation permanently"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {showCreate && (
        <Modal title="Create Organisation" onClose={() => setShowCreate(false)} wide dismissOnBackdrop={false}>
          <CreateOrgForm
            onCancel={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); load(); }}
          />
        </Modal>
      )}

      {inviteOrg && (
        <Modal title="Invite Admin" onClose={() => setInviteOrg(null)}>
          <InviteAdminForm
            org={inviteOrg}
            onCancel={() => setInviteOrg(null)}
            onDone={() => setInviteOrg(null)}
          />
        </Modal>
      )}

      {deleteOrg && (
        <Modal title="Delete Organisation" onClose={() => !deleteBusy && setDeleteOrg(null)}>
          <div className="space-y-3">
            <p className="text-sm text-white/80">
              Are you sure you want to delete{" "}
              <span className="text-white font-medium">{deleteOrg.name || deleteOrg.slug}</span>?
              This will permanently delete the organisation and all associated data.
              This cannot be undone.
            </p>

            {deleteOrg.user_count > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-200">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>
                  This organisation has{" "}
                  <strong>{deleteOrg.user_count}</strong> {deleteOrg.user_count === 1 ? "user" : "users"} who will also lose access.
                </span>
              </div>
            )}

            {deleteError && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-2 text-sm">
                {deleteError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setDeleteOrg(null)}
                disabled={deleteBusy}
                className={secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteOrg}
                disabled={deleteBusy}
                className={deleteFilledBtn}
              >
                <Trash2 size={14} />
                {deleteBusy ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <div
          className={
            "fixed bottom-6 right-6 z-[60] px-4 py-3 rounded-lg shadow-xl border " +
            (toast.tone === "ok"
              ? "bg-[#00FF99]/15 border-[#00FF99]/40 text-[#00FF99]"
              : "bg-yellow-500/15 border-yellow-500/40 text-yellow-200")
          }
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
